import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ZoomImage from '../../../ZoomImage/ZoonImage';
import { FileFieldProps } from '../../dynamicFormTypes';
import { getNameFromUrl, isImageUrl, isVideoUrl } from '../../formsUtils';
import { generateFormImageUrl } from '../../../../utils/forms.utils';
import FormFieldLinks from '../FormFieldLinks';

const FileField: React.FC<FileFieldProps> = ({
    value,
    label,
    error,
    onChange,
    src,
    alignLeft = false,
    links,
}) => {
    const [ preview, setPreview ] = useState<string | null>(null);

    const isDisplayOnly = Boolean(src);
    const isFile = value instanceof File;
    const isUrl = typeof value === 'string' && value.length > 0;

    const mime = isFile ? value.type : '';
    const isImage = isDisplayOnly || (
        isFile ? mime.startsWith('image/') : isUrl ? isImageUrl(value) : false
    );
    const isVideo = isFile ? mime.startsWith('video/') : isUrl ? isVideoUrl(value) : false;

    useEffect(() => {
        if (src) {
            setPreview(generateFormImageUrl(src));
            return;
        }

        if (!value) {
            setPreview(null);
            return;
        }

        if (value instanceof File) {
            if (value.type.startsWith('image/') || value.type.startsWith('video/')) {
                const url = URL.createObjectURL(value);
                setPreview(url);
                return () => URL.revokeObjectURL(url);
            }
            setPreview(null);
            return;
        }

        setPreview(isImageUrl(value) || isVideoUrl(value) ? value : null);
    }, [ src, value ]);

    const fileName = value instanceof File ? value.name : typeof value === 'string' ? getNameFromUrl(value) : '';

    return (
        <div style={{ display: 'flex', marginBottom: 8 }}>
            {!alignLeft && (
                <div style={{ width: 200, fontSize: 14, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    {label} 
                    {links && links.length > 0 && (
                        <FormFieldLinks links={links} />
                    )}
                </div>
            )}

            <div style={{ width: 300 }}>
                {!isDisplayOnly && (
                    <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }}>
                        Upload file
                        <input
                            hidden
                            type="file"
                            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
                        />
                    </Button>
                )}

                {preview && isImage && (
                    <ZoomImage
                        src={preview}
                        alt="Example"
                        zoomOnClick
                        style={{
                            display: 'block',
                            width: '100%',
                            maxHeight: 180,
                            objectFit: 'contain',
                            border: '1px solid rgba(0,0,0,0.23)',
                            borderRadius: 1,
                        }}
                    />
                )}

                {preview && isVideo && (
                    <Box
                        component="video"
                        src={preview}
                        controls
                        sx={{
                            display: 'block',
                            width: '100%',
                            maxHeight: 180,
                            border: '1px solid rgba(0,0,0,0.23)',
                            borderRadius: 1,
                        }}
                    />
                )}

                {!!value && !preview && (
                    <Typography variant="body2" color="text.secondary">
                        {fileName}
                    </Typography>
                )}

                {error && (
                    <Typography variant="caption" color="error">
                        {error}
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default FileField;